"use client";

import { useState, useRef, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import Image from "@/components/Image";
import { HiPlay, HiChevronLeft, HiChevronRight, HiX } from "@/components/icons";

interface DemoSlide {
  type: "image" | "video";
  src: string;
  poster?: string;
  alt?: string;
}

interface DemoGalleryProps {
  title: string;
  description: string;
  slides: DemoSlide[];
}

export default function DemoGallery({ title, description, slides }: DemoGalleryProps) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Don't render if no slides
  if (!slides || slides.length === 0) {
    return null;
  }

  const handleLightboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsLightboxOpen(false);
    }
  };

  const handleSlideChange = (newIndex: number) => {
    setVideoPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setActiveSlide(newIndex);
  };

  const nextSlide = () => {
    handleSlideChange(activeSlide === slides.length - 1 ? 0 : activeSlide + 1);
  };

  const prevSlide = () => {
    handleSlideChange(activeSlide === 0 ? slides.length - 1 : activeSlide - 1);
  };

  return (
    <section className="py-24 bg-background border-y border-border/50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tighter">{title}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">{description}</p>
        </div>

        <div className="max-w-4xl mx-auto relative group">
          <div className="overflow-hidden rounded-2xl border border-border shadow-2xl bg-black/5 aspect-video relative">
            {slides[activeSlide].type === "image" ? (
              <button
                type="button"
                className="relative w-full h-full cursor-zoom-in"
                onClick={() => setIsLightboxOpen(true)}
                aria-label="View image in fullscreen"
              >
                <Image
                  src={slides[activeSlide].src}
                  alt={slides[activeSlide].alt || "Product screenshot"}
                  fill
                  className="object-contain"
                  sizes="(max-width: 768px) 100vw, 1000px"
                />
              </button>
            ) : (
              <div className="w-full h-full relative">
                <video
                  ref={videoRef}
                  controls
                  poster={slides[activeSlide].poster}
                  className="w-full h-full object-contain"
                  onPlay={() => setVideoPlaying(true)}
                  onPause={() => setVideoPlaying(false)}
                  onEnded={() => setVideoPlaying(false)}
                >
                  <source src={slides[activeSlide].src} type="video/mp4" />
                </video>

                {!videoPlaying && (
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer"
                    onClick={() => videoRef.current?.play()}
                    aria-label="Play video"
                  >
                    <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                      <HiPlay className="w-10 h-10 text-black/80 ml-1" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Navigation Controls (only if multiple slides) */}
          {slides.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 hover:bg-background rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
                aria-label="Previous slide"
              >
                <HiChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-background/80 hover:bg-background rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next slide"
              >
                <HiChevronRight className="w-6 h-6" />
              </button>

              {/* Dots */}
              <div
                className="flex justify-center gap-2 mt-6"
                role="tablist"
                aria-label="Slide indicators"
              >
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSlideChange(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === activeSlide ? "bg-primary" : "bg-muted-foreground/30"}`}
                    aria-label={`Go to slide ${idx + 1}`}
                    aria-selected={idx === activeSlide}
                    role="tab"
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox Overlay - Portaled to body to escape z-index stacking contexts */}
      {isLightboxOpen && slides[activeSlide].type === "image" && (
        <Portal>
          <div
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200 backdrop-blur-sm"
            onClick={() => setIsLightboxOpen(false)}
            onKeyDown={handleLightboxKeyDown}
            role="dialog"
            aria-modal="true"
            aria-label="Image fullscreen view"
          >
            {/* Close Button - High Contrast */}
            <button
              className="absolute top-6 right-6 z-[10000] text-black bg-white hover:bg-white/90 rounded-full p-3 transition-transform hover:scale-110 shadow-lg cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setIsLightboxOpen(false);
              }}
              aria-label="Close fullscreen view"
            >
              <HiX className="w-6 h-6" />
            </button>

            <div className="relative w-full h-full max-w-7xl max-h-[90vh]">
              <Image
                src={slides[activeSlide].src}
                alt={slides[activeSlide].alt || "Product screenshot full view"}
                fill
                className="object-contain"
                sizes="100vw"
                priority
              />
            </div>
          </div>
        </Portal>
      )}
    </section>
  );
}

function Portal({ children }: { children: React.ReactNode }) {
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  if (!mounted) return null;

  return createPortal(children, document.body);
}
